"use client"

import { useEffect, useState } from "react"
import { MdLocationOn, MdWifiOff } from "react-icons/md"

interface OfflineAwareMapProps {
  src: string
  title: string
  className?: string
}

export function OfflineAwareMap({ src, title, className }: OfflineAwareMapProps) {
  const [isOnline, setIsOnline] = useState(true)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    setIsOnline(navigator.onLine)
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)
    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
    }
  }, [])

  if (!mounted || !isOnline) {
    return (
      <div
        className={`flex flex-col items-center justify-center gap-3 bg-gray-100 dark:bg-gray-800 rounded-lg ${className}`}
        suppressHydrationWarning
      >
        <div className="flex flex-col items-center gap-2 text-gray-400 dark:text-gray-500">
          <MdLocationOn size={40} />
          <MdWifiOff size={24} />
        </div>
        <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Map unavailable offline</p>
        <p className="text-gray-400 dark:text-gray-500 text-xs">Reconnect to view the location</p>
      </div>
    )
  }

  return (
    <iframe
      src={src}
      width="100%"
      height="100%"
      style={{ border: 0 }}
      allowFullScreen
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
      className={`transition-all duration-300 hover:brightness-110 ${className ?? ""}`}
      title={title}
    />
  )
}
